import { Select } from '@mantine/core'
import type {
  TSoilCurrentValueMapOption,
  TSoilCurrentValueMapOptionSingleDepth,
} from 'App/Map/CurrentValuesMap/_utils/by-domain/soil'
import type { TValuesCurrentPoints } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { removeCmOrInFromSoilProbeDepths } from 'utils/removeCmOrInFromSoilProbeDepths'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const updateMapVisuals = (newValueType: TValuesCurrentPoints) => {
  mapControlsStore.setState((s) => ({
    ...s,
    mapVisualValueGroup: {
      ...s.mapVisualValueGroup,
      [MAP_VISUAL.POINT]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.POINT],
        soil: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.POINT].soil ?? { mItem: 'M_InBlockPoint' }),
          valueType: newValueType,
        },
      },
    },
  }))
}

export const SoilSecondaryDropdown = () => {
  const depthUnit = userDetailsStore.useSelector((s) => s.depth)

  const value = mapControlsStore.useSelector(
    (s) => s.mapVisualValueGroup?.[MAP_VISUAL.POINT]?.soil?.valueType,
  )

  const properties = fieldAssetStore.useSelector((s) => s.properties)

  const data: { group: string; items: { value: TSoilCurrentValueMapOption; label: string }[] }[] =
    useMemo(() => {
      const allAvailableProbeDepths = new Set<TSoilCurrentValueMapOptionSingleDepth>()

      Object.values(properties ?? {}).forEach((p) => {
        if (!p.points) return

        Object.values(p.points).forEach((ss) => {
          if (!ss.configuration.soilProbeDepthsAvailable) return

          ss.configuration.soilProbeDepthsAvailable.forEach((pd) => allAvailableProbeDepths.add(pd))
        })
      })

      const returner: { group: string; items: { value: TSoilCurrentValueMapOption; label: string }[] }[] = [
        {
          group: translate.phrases.banyanApp('Multiple Depths'),
          items: [
            {
              value: 'DEFAULT',
              label: translate.phrases.banyanApp('Root Zone Average'),
            },
          ],
        },
      ]

      const probeDepthsArray = [...allAvailableProbeDepths].sort(alphabeticalSort)

      if (probeDepthsArray.length > 0) {
        returner.push({
          group: translate.phrases.banyanApp('Individual Depths (Where Available)'),
          items: probeDepthsArray.map((pd) => ({
            value: pd,
            label: unitConverter.soilDepth(removeCmOrInFromSoilProbeDepths(pd)).valueWithSuffix(),
          })),
        })
      }

      return returner
    }, [depthUnit, properties, value])

  return (
    <Select
      data={data}
      value={value}
      onChange={(newValueType) => {
        if (newValueType) updateMapVisuals(newValueType as TValuesCurrentPoints)
      }}
      styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
      comboboxProps={{ withinPortal: true }}
    />
  )
}
